import {
	ActionIcon,
	Alert,
	Badge,
	Button,
	Group,
	Pagination,
	Paper,
	Stack,
	Table,
	Text,
	TextInput,
	Title,
	Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconAlertCircle, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSnapshot } from "valtio";
import { allowedEmailTexts } from "@/locales/allowed-email";
import { i18nStore } from "@/store/i18n";
import {
	addAllowedEmail,
	fetchAllowedEmails,
	removeAllowedEmail,
} from "./allowed-email.api";
import { AllowedEmailUserActions } from "./allowed-email-user-actions";

const PAGE_SIZE = 10;
const QUERY_KEY = "admin-allowed-emails";

/** Panel admin: kelola allowlist email demo (auto-verify saat login OAuth). */
export function AllowedEmailPanel() {
	const { lang } = useSnapshot(i18nStore);
	const text = allowedEmailTexts[lang === "en" ? "en" : "id"];
	const queryClient = useQueryClient();
	const [page, setPage] = useState(1);
	const [email, setEmail] = useState("");
	const [note, setNote] = useState("");
	const [feedback, setFeedback] = useState<{
		color: "red" | "green";
		message: string;
	} | null>(null);

	const list = useQuery({
		queryKey: [QUERY_KEY, page],
		queryFn: () => fetchAllowedEmails(page, PAGE_SIZE),
	});
	const refresh = () =>
		queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
	const fail = (error: Error) =>
		setFeedback({ color: "red", message: error.message || text.failed });

	const add = useMutation({
		mutationFn: () => addAllowedEmail(email, note.trim()),
		onSuccess: () => {
			setEmail("");
			setNote("");
			setPage(1);
			setFeedback({ color: "green", message: text.addedOk });
			refresh();
		},
		onError: fail,
	});
	const remove = useMutation({
		mutationFn: removeAllowedEmail,
		onSuccess: () => {
			setFeedback({ color: "green", message: text.removedOk });
			refresh();
		},
		onError: fail,
	});

	const confirmRemove = (id: string) =>
		modals.openConfirmModal({
			title: text.removeConfirmTitle,
			children: <Text size="sm">{text.removeConfirmBody}</Text>,
			labels: { confirm: text.remove, cancel: text.cancel },
			confirmProps: { color: "red" },
			onConfirm: () => remove.mutate(id),
		});

	const totalPages = Math.max(
		1,
		Math.ceil((list.data?.total ?? 0) / PAGE_SIZE),
	);
	const rows = list.data?.data ?? [];

	return (
		<Paper withBorder p="md" radius="md" mt="xl">
			<Stack gap="sm">
				<div>
					<Title order={4}>{text.title}</Title>
					<Text size="sm" c="dimmed">
						{text.description}
					</Text>
				</div>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						add.mutate();
					}}
				>
					<Group align="flex-end" wrap="wrap">
						<TextInput
							label={text.emailLabel}
							placeholder={text.emailPlaceholder}
							type="email"
							required
							value={email}
							onChange={(e) => setEmail(e.currentTarget.value)}
						/>
						<TextInput
							label={text.noteLabel}
							placeholder={text.notePlaceholder}
							value={note}
							onChange={(e) => setNote(e.currentTarget.value)}
						/>
						<Button type="submit" color="orange" loading={add.isPending}>
							{text.add}
						</Button>
					</Group>
				</form>

				{feedback && (
					<Alert
						color={feedback.color}
						withCloseButton
						onClose={() => setFeedback(null)}
					>
						{feedback.message}
					</Alert>
				)}

				{list.isError && (
					<Alert icon={<IconAlertCircle size={16} />} color="red">
						{text.loadFailed}
					</Alert>
				)}

				{!list.isError && !list.isLoading && rows.length === 0 && (
					<Text c="dimmed" ta="center" py="md">
						{text.empty}
					</Text>
				)}

				{rows.length > 0 && (
					<Table.ScrollContainer minWidth={760}>
						<Table verticalSpacing="xs">
							<Table.Thead>
								<Table.Tr>
									<Table.Th>{text.columns.email}</Table.Th>
									<Table.Th>{text.columns.note}</Table.Th>
									<Table.Th>{text.columns.status}</Table.Th>
									<Table.Th>{text.columns.access}</Table.Th>
									<Table.Th>{text.columns.actions}</Table.Th>
								</Table.Tr>
							</Table.Thead>
							<Table.Tbody>
								{rows.map((row) => (
									<Table.Tr key={row.id}>
										<Table.Td>{row.email}</Table.Td>
										<Table.Td>{row.note ?? "-"}</Table.Td>
										<Table.Td>
											{row.usedAt ? (
												<Badge color="green" variant="light">
													{text.usedAt.replace(
														"{date}",
														new Date(row.usedAt).toLocaleDateString(
															lang === "en" ? "en-US" : "id-ID",
														),
													)}
												</Badge>
											) : (
												<Badge color="gray" variant="light">
													{text.unused}
												</Badge>
											)}
										</Table.Td>
										<Table.Td>
											{row.user ? (
												<AllowedEmailUserActions
													user={row.user}
													text={text}
													onChanged={(message) => {
														setFeedback({ color: "green", message });
														refresh();
													}}
													onError={fail}
												/>
											) : (
												<Text size="xs" c="dimmed">
													{text.noAccount}
												</Text>
											)}
										</Table.Td>
										<Table.Td>
											<Tooltip label={text.remove}>
												<ActionIcon
													variant="light"
													color="red"
													aria-label={text.remove}
													onClick={() => confirmRemove(row.id)}
												>
													<IconTrash size={16} />
												</ActionIcon>
											</Tooltip>
										</Table.Td>
									</Table.Tr>
								))}
							</Table.Tbody>
						</Table>
					</Table.ScrollContainer>
				)}

				{totalPages > 1 && (
					<Group justify="center">
						<Pagination value={page} onChange={setPage} total={totalPages} />
					</Group>
				)}
			</Stack>
		</Paper>
	);
}
